import { useEffect, useRef } from 'react';
import { createAnalyticsController } from '../lib/analyticsGating';
import { hasConsent, subscribeConsent } from '../lib/consent';
import { TELEMETRY_URL } from '../utils/api';

const HEARTBEAT_MS = 60 * 60 * 1000;
const INACTIVE_MS = 5 * 60 * 1000;

export function useTelemetryHeartbeat() {
  const lastActivity = useRef(0);

  // GDPR: il ping manda un fingerprint del device -> non e' essenziale, parte SOLO con
  // consenso analytics (default: negato), come telemetria/Sentry in main.tsx. Revoca = stop.
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let firstPingId: ReturnType<typeof setTimeout> | undefined;
    lastActivity.current = Date.now();

    const ping = async () => {
      if (Date.now() - lastActivity.current > INACTIVE_MS) return;
      let fingerprint = 'browser';
      const electronAPI = (window as unknown as { electronAPI?: { getFingerprint?: () => Promise<string> } }).electronAPI;
      if (electronAPI?.getFingerprint) {
        try {
          fingerprint = await electronAPI.getFingerprint();
        } catch {
          // ignore fingerprint errors
        }
      }
      fetch(`${TELEMETRY_URL}/api/telemetry/ping`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fingerprint }),
      }).catch(() => {});
    };

    const onActivity = () => { lastActivity.current = Date.now(); };

    const start = () => {
      if (intervalId !== undefined) return;
      window.addEventListener('mousemove', onActivity, { passive: true });
      window.addEventListener('keydown', onActivity, { passive: true });
      window.addEventListener('click', onActivity, { passive: true });
      intervalId = setInterval(ping, HEARTBEAT_MS);
      firstPingId = setTimeout(ping, 5000);
    };

    const stop = () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('click', onActivity);
      if (intervalId !== undefined) clearInterval(intervalId);
      if (firstPingId !== undefined) clearTimeout(firstPingId);
      intervalId = undefined;
      firstPingId = undefined;
    };

    const controller = createAnalyticsController({ start, stop });
    const unsubscribe = subscribeConsent((state) => controller.apply(state.categories.analytics));
    controller.apply(hasConsent('analytics'));
    return () => {
      unsubscribe();
      controller.stop();
    };
  }, []);
}
